var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { LayoutBase } from './components/LayoutBase';
import { LayoutForm } from './components/LayoutForm';
/**
 * `Layout` is a high-level generic React component designed to provide a flexible layout structure for different types of pages within an application.
 * It acts as a wrapper component that can render either a basic layout or a form layout, depending on the props provided.
 * It integrates `LayoutBase` component for basic layouts and `LayoutForm` component for form-based layouts.
 *
 * @template TData - The type of data to be handled by the layout, especially for forms. It should extend from FieldValues of 'react-hook-form'.
 * @param {LayoutProps<TData>} props - The properties passed to the Layout component, which determine the layout's behavior and content.
 *
 * Props can include:
 * - For basic layout:
 *   @param {React.ReactNode} header - The header content.
 *   @param {React.ReactNode} content - The main content of the layout.
 *   @param {React.ReactNode} breadcrumbs - Breadcrumb links for navigation.
 *   @param {boolean} isLoading - Loading state of the layout.
 * - For form layout:
 *   @param {TData} data - Initial data for form fields.
 *   @param {(data: TData) => void} onSubmit - Function to handle form submission.
 */
export const Layout = (props) => {
    const body = useMemo(() => {
        if (props.isForm) {
            const _a = props, { data, onSubmit, onError } = _a, rest = __rest(_a, ["data", "onSubmit", "onError"]);
            return (_jsx(LayoutForm, Object.assign({ onSubmit: onSubmit, onError: onError, isDisabled: props.isDisabled, data: data }, { children: ({ control, getValues, setValue, watch, errors }) => (_jsx(LayoutBase, Object.assign({ control: control, getValues: getValues, setValue: setValue, watch: watch, errors: errors }, rest))) })));
        }
        else {
            return _jsx(LayoutBase, Object.assign({}, props));
        }
    }, [props]);
    return body;
};
